import { useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';

import './ResizablePanel.css';

export default function ResizablePanel(props) {
  const { topPanel, bottomPanel } = props;

  const topPanelRef = useRef();
  const bottomPanelRef = useRef();
  const dragPanelRef = useRef();

  const [windowHeight, setWindowHeight] = useState();
  const [topHeight, setTopHeight] = useState();
  const [bottomHeight, setBottomHeight] = useState();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [resizing, setResizing] = useState(false);

  const minimumTopPanelHeight = 100;
  const initialBottomPanelRatio = 0.35;

  useEffect(() => {
    const header = document.querySelector('.app > header');
    if (!header) return;

    function handleHeaderResize() {
      setHeaderHeight(header.clientHeight);
    }

    handleHeaderResize();
    const observer = new ResizeObserver(handleHeaderResize);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    function handleWindowResize() {
      setWindowHeight(document.body.parentElement.clientHeight);
    }

    handleWindowResize();
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  useEffect(() => {
    if (topHeight >= 0) topPanelRef.current.style.height = `${topHeight}px`;
  }, [topHeight]);

  useEffect(() => {
    if (bottomHeight >= 0)
      bottomPanelRef.current.style.height = `${bottomHeight}px`;
  }, [bottomHeight]);

  useEffect(() => {
    if (bottomHeight >= 0) {
      setTopHeight(windowHeight - headerHeight - bottomHeight);
    }
  }, [bottomHeight, headerHeight, windowHeight]);

  useEffect(() => {
    if (bottomHeight === undefined && windowHeight) {
      setBottomHeight(windowHeight * initialBottomPanelRatio);
    }
  }, [bottomHeight, topHeight, windowHeight]);

  useEffect(() => {
    function handleMouseDown(e) {
      setResizing({
        startY: e.clientY,
        bottomStart: bottomHeight,
        topStart: topHeight,
      });
    }

    function handleMouseMove(e) {
      if (!resizing) return;
      const delta = resizing.startY - e.clientY;
      const newBottomHeight = resizing.bottomStart + delta;
      const newTopHeight = resizing.topStart - delta;

      if (newTopHeight > minimumTopPanelHeight) {
        setBottomHeight(newBottomHeight);
        setTopHeight(newTopHeight);
      }
    }

    function handleMouseUp() {
      setResizing(null);
    }

    const dragHandle = dragPanelRef.current;

    dragHandle.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      dragHandle.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, bottomHeight, topHeight]);

  function handleToggle() {
    if (topHeight <= minimumTopPanelHeight) {
      setBottomHeight(windowHeight * initialBottomPanelRatio);
    } else if (bottomHeight === 0) {
      setBottomHeight(windowHeight - headerHeight - minimumTopPanelHeight);
    } else {
      setBottomHeight(0);
    }
  }

  return (
    <Box sx={{ height: '100%' }}>
      <Box sx={{ height: '65%' }} className="top-panel" ref={topPanelRef}>
        {topPanel}
      </Box>
      <Box
        sx={{ height: '35%' }}
        className={`bottom-panel ${
          bottomHeight ? 'bottom-open ' : 'bottom-close'
        } `}
        ref={bottomPanelRef}
      >
        <Box className="resizable-panel-move" id="drag" ref={dragPanelRef}>
          <Box className="resizable-panel-thumb" />
        </Box>
        <Box
          onClick={handleToggle}
          className={`open-close-button ${bottomHeight ? 'open' : 'close'}`}
          title="Hide Attribute Table"
        />
        {bottomPanel}
      </Box>
    </Box>
  );
}
